import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  addToast,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAddProductRepository, useCreateProduct } from '~/hooks/useProducts';
import { useRepositories } from '~/hooks/useRepositories';

import { Input, Textarea } from '~/components/Field';
import { FilterBar } from '~/components/layout/FilterBar';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal } from '~/components/Modal';

import type { Product, Repository } from '~/types';

/**
 * Zod schema for product creation form.
 * Pattern: docs/design-patterns/frontend/form-handling.md
 */
const newProductSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Product name is required')
    .max(255, 'Product name must be less than 255 characters'),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional()
    .or(z.literal('')),
});

type NewProductFormData = z.infer<typeof newProductSchema>;

/** Sentinel key for the "All owners" filter option — HeroUI Select keys can't be empty strings. */
const ALL_OWNERS = '__all__';

/** Tracks a create that partially failed to link repos, so Retry/Skip know what to act on. */
interface PartialFailureState {
  failedRepos: Repository[];
  product: Product;
}

export interface NewProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the product (and any successfully linked repos) are settled. */
  onCreated?: (product: Product) => void;
  /** Repos to pre-check in the multi-select. */
  preselectedRepoIds?: string[];
}

export function NewProductModal({
  isOpen,
  onClose,
  onCreated,
  preselectedRepoIds = [],
}: NewProductModalProps): JSX.Element {
  const navigate = useNavigate();
  const { data: repositories } = useRepositories();
  const createProduct = useCreateProduct();
  const addProductRepository = useAddProductRepository();

  const [selectedRepoIds, setSelectedRepoIds] = useState<string[]>(preselectedRepoIds);
  const [ownerFilter, setOwnerFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [partialFailure, setPartialFailure] = useState<PartialFailureState | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  const owners = useMemo(
    () =>
      [...new Set((repositories ?? []).map(repository => repository.owner))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [repositories],
  );

  const filteredRepositories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (repositories ?? []).filter(repository => {
      const matchesOwner = !ownerFilter || repository.owner === ownerFilter;
      const matchesQuery = !query || repository.fullName.toLowerCase().includes(query);
      return matchesOwner && matchesQuery;
    });
  }, [repositories, ownerFilter, searchQuery]);

  // "Select all" always targets the currently filtered/searched set, not the
  // full repo list — narrowing the list and selecting all shouldn't reach
  // into repos the search/owner filter is hiding.
  const allFilteredSelected =
    filteredRepositories.length > 0 &&
    filteredRepositories.every(repository => selectedRepoIds.includes(repository.id));
  const someFilteredSelected = filteredRepositories.some(repository =>
    selectedRepoIds.includes(repository.id),
  );

  const handleSelectAllChange = (checked: boolean): void => {
    const filteredIds = new Set(filteredRepositories.map(repository => repository.id));
    setSelectedRepoIds(prev =>
      checked ? [...new Set([...filteredIds, ...prev])] : prev.filter(id => !filteredIds.has(id)),
    );
  };

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<NewProductFormData>({
    resolver: zodResolver(newProductSchema),
    mode: 'onBlur',
    defaultValues: { name: '', description: '' },
  });

  // Extract ref/onBlur from register (onChange is filtered by HeroUI, so we use onValueChange).
  const { onBlur: nameOnBlur, ref: nameRef } = register('name');
  const name = watch('name');

  // Reset all local state on open/close transitions so a reopen always starts clean and a
  // fresh preselection takes effect.
  useEffect(() => {
    if (!isOpen) {
      reset();
      setPartialFailure(null);
      setIsLinking(false);
      setOwnerFilter('');
      setSearchQuery('');
    }
    setSelectedRepoIds(preselectedRepoIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync on open/close, not every preselectedRepoIds identity change
  }, [isOpen]);

  const isSubmitting = createProduct.isPending || isLinking;

  const finishAndNavigate = (product: Product): void => {
    onCreated?.(product);
    onClose();
    void navigate(`/products/${product.id}`);
  };

  /**
   * Link `repoIds` to `product`, letting every request settle (Promise.allSettled) before
   * deciding success vs. partial failure — a single rejected link must never abort the others
   * or leave the product stuck with only some intended repos attached.
   */
  const linkRepositories = async (product: Product, repoIds: string[]): Promise<void> => {
    if (repoIds.length === 0) {
      finishAndNavigate(product);
      return;
    }

    setIsLinking(true);
    const results = await Promise.allSettled(
      repoIds.map(repositoryId =>
        addProductRepository.mutateAsync({ productId: product.id, repositoryId }),
      ),
    );
    setIsLinking(false);

    const failedIds = repoIds.filter((_repoId, index) => results[index]?.status === 'rejected');
    if (failedIds.length === 0) {
      finishAndNavigate(product);
      return;
    }

    const failedRepos = (repositories ?? []).filter(repo => failedIds.includes(repo.id));
    setPartialFailure({ failedRepos, product });
  };

  const onSubmit = (data: NewProductFormData): void => {
    createProduct.mutate(
      { description: data.description || undefined, name: data.name },
      {
        onError: (error: Error) => {
          addToast({ color: 'danger', description: error.message || 'Failed to create product' });
        },
        onSuccess: product => {
          void linkRepositories(product, selectedRepoIds);
        },
      },
    );
  };

  const handleRetryFailed = (): void => {
    if (!partialFailure) return;
    const retryIds = partialFailure.failedRepos.map(repo => repo.id);
    setPartialFailure(null);
    void linkRepositories(partialFailure.product, retryIds);
  };

  const handleSkipAndContinue = (): void => {
    if (!partialFailure) return;
    finishAndNavigate(partialFailure.product);
  };

  const handleClose = (): void => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const isNameValid = !!name?.trim() && !errors.name;

  return (
    <Modal isOpen={isOpen} maxWidth="3xl" preventClosing={isSubmitting} onOpenChange={handleClose}>
      <ModalContent>
        <form
          aria-busy={isSubmitting}
          className="contents"
          noValidate
          onSubmit={e => void handleSubmit(onSubmit)(e)}
        >
          <ModalHeader>Create Product</ModalHeader>
          <Divider />
          <ModalBody>
            <Input
              ref={nameRef}
              errorMessage={errors.name?.message}
              id="new-product-name"
              isDisabled={isSubmitting}
              isInvalid={!!errors.name}
              isRequired
              label="Name"
              labelPlacement="outside-top"
              name="name"
              placeholder="Compounds Platform"
              value={name}
              onBlur={e => void nameOnBlur(e)}
              onValueChange={value => {
                setValue('name', value, { shouldDirty: true });
              }}
            />

            <Textarea
              errorMessage={errors.description?.message}
              id="new-product-description"
              isDisabled={isSubmitting}
              isInvalid={!!errors.description}
              label="Description"
              labelPlacement="outside-top"
              placeholder="Describe this product (optional)"
              {...register('description')}
            />

            {(repositories?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-small text-foreground-500">
                  Select the repositories that make up this product.
                </span>
                <FilterBar
                  controls={
                    <Select
                      aria-label="Filter by owner"
                      className="w-40"
                      disallowEmptySelection
                      isDisabled={isSubmitting}
                      selectedKeys={[ownerFilter || ALL_OWNERS]}
                      size="sm"
                      onSelectionChange={keys =>
                        setOwnerFilter(
                          ([...keys][0] as string) === ALL_OWNERS ? '' : ([...keys][0] as string),
                        )
                      }
                    >
                      {[
                        <SelectItem key={ALL_OWNERS}>All owners</SelectItem>,
                        ...owners.map(owner => <SelectItem key={owner}>{owner}</SelectItem>),
                      ]}
                    </Select>
                  }
                  count={{ shown: filteredRepositories.length, total: repositories?.length ?? 0 }}
                  searchPlaceholder="Search repositories..."
                  searchValue={searchQuery}
                  onSearchChange={setSearchQuery}
                />
                <div className="flex max-h-64 w-full flex-col overflow-hidden rounded-lg border border-default">
                  <div className="flex shrink-0 items-center gap-2 border-b border-default bg-surface-elevated px-3 py-2">
                    <Checkbox
                      classNames={{
                        // The header row's own bg-surface-elevated background washes out
                        // the checkbox's default (subtle) border — force a visible one.
                        wrapper: 'before:border-foreground/50',
                      }}
                      isDisabled={isSubmitting || filteredRepositories.length === 0}
                      isIndeterminate={someFilteredSelected && !allFilteredSelected}
                      isSelected={allFilteredSelected}
                      onValueChange={handleSelectAllChange}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                        Select all
                      </span>
                    </Checkbox>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3">
                    {filteredRepositories.length === 0 ? (
                      <p className="text-sm text-muted">No repositories match your search.</p>
                    ) : (
                      <CheckboxGroup
                        aria-label="Link repositories"
                        isDisabled={isSubmitting}
                        value={selectedRepoIds}
                        onValueChange={setSelectedRepoIds}
                      >
                        {filteredRepositories.map(repo => (
                          <Checkbox key={repo.id} value={repo.id}>
                            {repo.fullName}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    )}
                  </div>
                </div>
              </div>
            )}

            {partialFailure && (
              <div
                className="rounded-md border border-warning/40 bg-warning-100 p-3 text-sm text-warning-700 dark:bg-warning-200 dark:text-warning-800"
                role="alert"
              >
                <p className="font-medium">
                  &ldquo;{partialFailure.product.name}&rdquo; was created, but{' '}
                  {partialFailure.failedRepos.length} repositor
                  {partialFailure.failedRepos.length !== 1 ? 'ies' : 'y'} failed to link:
                </p>
                <ul className="mt-1 list-inside list-disc">
                  {partialFailure.failedRepos.map(repo => (
                    <li key={repo.id}>{repo.fullName}</li>
                  ))}
                </ul>
              </div>
            )}
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button isDisabled={isSubmitting} type="button" variant="light" onPress={handleClose}>
              Cancel
            </Button>
            {partialFailure ? (
              <>
                <Button
                  data-testid="new-product-skip-continue"
                  isDisabled={isSubmitting}
                  type="button"
                  variant="flat"
                  onPress={handleSkipAndContinue}
                >
                  Skip and continue
                </Button>
                <Button
                  aria-busy={isSubmitting}
                  color="primary"
                  data-testid="new-product-retry-failed"
                  isDisabled={isSubmitting}
                  type="button"
                  onPress={handleRetryFailed}
                >
                  {isSubmitting ? <LoadingSpinner /> : 'Retry failed'}
                </Button>
              </>
            ) : (
              <Button
                aria-busy={isSubmitting}
                color="primary"
                data-testid="new-product-submit"
                isDisabled={isSubmitting || !isNameValid}
                type="submit"
              >
                {isSubmitting ? <LoadingSpinner /> : 'Create'}
              </Button>
            )}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
